import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAgentUrl } from "./dev-agent-url";
import {
  MAX_RECORDING_MS,
  type VoiceInput,
  type VoiceState,
} from "./voice-input";

// A clinic uplink stalling mid-upload otherwise leaves the composer transcribing forever, with
// both the mic and Send held — no way to answer at all. Generous enough that a slow link finishes.
export const UPLOAD_TIMEOUT_MS = 30_000;
// The display only shows whole seconds, and elapsedMs lives in state that OnboardingConversation
// owns — so a faster tick re-renders the whole conversation, cards and all, for every tick of a
// recording that can run a minute.
const TICK_MS = 1_000;

// Without this, a future reader drops the option and lets the browser pick its default ~128kbps,
// which turns a one-minute recording from ~180KB into ~1MB and a three-second upload into twenty
// on the ~500kbps uplink these clinics actually have.
const AUDIO_BITS_PER_SECOND = 24_000;

// Without this probe a reader hardcodes "audio/webm", which Safari does not support — MediaRecorder
// throws on construction and voice is dead on every iPhone.
const PREFERRED_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const supportedMimeType = (): string | undefined =>
  typeof MediaRecorder === "undefined"
    ? undefined
    : PREFERRED_TYPES.find((type) => MediaRecorder.isTypeSupported(type));

// NotFoundError is the only one that means there is no microphone. NotReadableError (another app
// holds it) and OverconstrainedError are ordinary on a phone, and telling someone their device has
// no microphone when it is merely busy sends them looking for the wrong problem.
const startFailureMessage = (error: unknown): string =>
  error instanceof DOMException && error.name === "NotFoundError"
    ? "No microphone found. Type it instead?"
    : "Could not use the microphone. Type it instead?";

const canRecord = (): boolean =>
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  supportedMimeType() !== undefined;

export const useAgentVoiceInput = (): VoiceInput => {
  const agentUrl = useAgentUrl();
  const { getAccessTokenSilently } = useAuth0();
  const getToken = useRef(getAccessTokenSilently);
  getToken.current = getAccessTokenSilently;

  const [supported] = useState(canRecord);
  const [transcript, setTranscript] = useState<string>();
  const [state, setState] = useState<VoiceState>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [message, setMessage] = useState<string>();

  const recorderRef = useRef<MediaRecorder>();
  // Without a per-attempt identity that every scheduled callback rechecks, three paths upload
  // audio nobody asked to send: cancelling during the permission prompt, unmounting mid-recording,
  // and a cancel-then-restart whose old onstop reads the new attempt's state.
  const attemptRef = useRef(0);
  const uploadRef = useRef<AbortController>();

  // Without stopping every track the phone's recording indicator stays lit after the recorder is
  // inactive, which reads as the app still listening.
  const release = useCallback(() => {
    attemptRef.current += 1;
    uploadRef.current?.abort();
    uploadRef.current = undefined;
    const recorder = recorderRef.current;
    recorderRef.current = undefined;
    if (!recorder) return;
    for (const track of recorder.stream.getTracks()) track.stop();
    if (recorder.state !== "inactive") recorder.stop();
  }, []);

  useEffect(() => release, [release]);

  const send = useCallback(
    async (audio: Blob, attempt: number) => {
      if (!agentUrl) return;
      const upload = new AbortController();
      uploadRef.current = upload;
      const current = () => attemptRef.current === attempt;
      let timedOut = false;
      const deadline = setTimeout(() => {
        timedOut = true;
        upload.abort();
      }, UPLOAD_TIMEOUT_MS);
      setState("transcribing");
      try {
        // The deadline aborts the controller, and the token has to lose to it as well: aborting
        // cannot cancel a stalled Auth0 promise, so without this race a hanging token never
        // reaches the fetch or the catch and leaves the composer transcribing for good.
        const abandoned = new Promise<never>((_, reject) => {
          upload.signal.addEventListener("abort", () =>
            reject(new DOMException("aborted", "AbortError")),
          );
        });
        const token = await Promise.race([getToken.current(), abandoned]);
        const response = await fetch(
          `${agentUrl.replace(/\/$/, "")}/transcribe`,
          {
            method: "POST",
            headers: {
              "content-type": audio.type,
              "x-epocus-on-behalf-of-token": token,
            },
            body: audio,
            signal: upload.signal,
          },
        );
        if (!current()) return;
        if (!response.ok) {
          setState("error");
          setMessage(
            response.status === 413
              ? "That recording was too long. Try a shorter one."
              : "Could not transcribe that. Type it instead?",
          );
          return;
        }
        const body = (await response.json().catch(() => undefined)) as
          | { text?: string }
          | undefined;
        if (!current()) return;
        if (!body) {
          setState("error");
          setMessage("Could not transcribe that. Type it instead?");
          return;
        }
        const { text } = body;
        const said = text?.trim();
        if (!said) {
          setState("error");
          setMessage("Didn't catch that. Try again, or type it instead.");
          return;
        }
        setState("idle");
        setMessage(undefined);
        setTranscript(said);
      } catch {
        if (!current()) return;
        setState("error");
        setMessage(
          timedOut
            ? "That took too long to send. Try again, or type it instead."
            : "Could not send that recording. Type it instead?",
        );
      } finally {
        clearTimeout(deadline);
      }
    },
    [agentUrl],
  );

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = undefined;
    if (recorder?.state === "recording") recorder.stop();
  }, []);

  const start = useCallback(() => {
    setState("starting");
    setMessage(undefined);
    setElapsedMs(0);
    attemptRef.current += 1;
    const attempt = attemptRef.current;

    void (async () => {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
          },
        });
      } catch (error) {
        if (attemptRef.current !== attempt) return;
        // Without separating this a reader shows the retry copy for a denial, which sends the
        // clinician back to a button that cannot work until they change a browser setting.
        const denied =
          error instanceof DOMException &&
          (error.name === "NotAllowedError" || error.name === "SecurityError");
        setState(denied ? "denied" : "error");
        if (!denied) setMessage(startFailureMessage(error));
        return;
      }

      if (attemptRef.current !== attempt) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }

      const mimeType = supportedMimeType();
      try {
        const recorder = new MediaRecorder(stream, {
          mimeType,
          audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
        });
        const chunks: Blob[] = [];
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.push(event.data);
        };
        recorder.onstop = () => {
          for (const track of stream.getTracks()) track.stop();
          if (attemptRef.current !== attempt) return;
          void send(new Blob(chunks, { type: mimeType }), attempt);
        };
        // MediaRecorder can also fail asynchronously — the input track disappearing mid-recording
        // is the usual cause. Invalidating the attempt is what stops the onstop that follows from
        // uploading a truncated take as though the clinician had finished speaking.
        recorder.onerror = () => {
          for (const track of stream.getTracks()) track.stop();
          if (attemptRef.current !== attempt) return;
          attemptRef.current += 1;
          recorderRef.current = undefined;
          setState("error");
          setMessage("Recording stopped unexpectedly. Type it instead?");
        };

        recorderRef.current = recorder;
        recorder.start();
        setState("recording");
      } catch {
        // The "starting" state renders neither a mic nor a text field, so a throw left unhandled
        // here strands the clinician with no way to answer at all and the microphone still open.
        for (const track of stream.getTracks()) track.stop();
        if (attemptRef.current !== attempt) return;
        setState("error");
        setMessage("Could not start recording. Type it instead?");
      }
    })();
  }, [send]);

  const cancel = useCallback(() => {
    release();
    setState("idle");
    setMessage(undefined);
  }, [release]);

  const clearTranscript = useCallback(() => setTranscript(undefined), []);

  useEffect(() => {
    if (state !== "recording") return;
    // clearInterval cannot un-queue a tick that has already fired, and stop() reads the shared
    // recorder ref — so without this an abandoned take's last tick stops the one that replaced it.
    const attempt = attemptRef.current;
    const startedAt = performance.now();
    const timer = setInterval(() => {
      if (attemptRef.current !== attempt) return;
      setElapsedMs(performance.now() - startedAt);
    }, TICK_MS);
    // A one-shot deadline rather than a comparison inside the tick: the cap would otherwise depend
    // on the interval firing punctually, and a delayed tick would let the take run past the limit.
    const cap = setTimeout(() => {
      if (attemptRef.current !== attempt) return;
      stop();
    }, MAX_RECORDING_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(cap);
    };
  }, [state, stop]);

  return {
    available: supported && Boolean(agentUrl),
    state,
    elapsedMs,
    message,
    transcript,
    start,
    stop,
    cancel,
    clearTranscript,
  };
};
